import { useRef, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { posthog } from 'posthog-js';
import { POSTHOG_EVENTS } from 'constants/posthogEvents';
import { fetchUnifiedAIStream } from '../../../utils/apiHooks';
import { AVAILABLE_DATA_SOURCES } from '../../../utils/mockDataRegistry';
import { generateWidgetSummaries } from '../../../utils/widgetSummary';
import { buildChatPageContext } from '../chatPageContext';

/** Normalize API thoughts to string[] (may be string or array). */
function normalizeThoughts(thoughts) {
    if (thoughts == null) return [];
    if (Array.isArray(thoughts)) return thoughts.filter((t) => t != null && String(t).trim()).map(String);
    return [String(thoughts).trim()].filter(Boolean);
}

/**
 * Send message flow: API call, Phase 1/2 handling, PII handling.
 * Uses a ref for handleSendMessage so it can call itself recursively for Phase 2.
 */
export function useSendMessage(deps) {
    const {
        messagesRef,
        setMessages,
        currentPhase,
        setCurrentPhase,
        setPendingWidgetId,
        pendingWidgetIdRef,
        getRecentHistory,
        getFullHistory,
        setError,
        setPendingPiiResolution,
        setPiiSearchQueries,
        setInputValue,
        inputRef,
        setIsTyping,
        state,
        exchangeIdRef,
    } = deps;

    const location = useLocation();
    const params = useParams();
    const currentClass = useSelector((s) => s.classes?.currentClass);
    const userId = useSelector((s) => s.auth?.teacher?.id);
    const schoolId = useSelector(
        (s) => s.auth?.teacher?.schoolId ?? s.auth?.teacher?.user_groups?.schoolId ?? s.auth?.teacher?.groupId,
    );

    const pageContext = buildChatPageContext({
        pathname: location?.pathname ?? '',
        params: params ?? {},
        currentClass,
        report: state?.report ?? null,
    });

    const handleSendMessageRef = useRef(null);

    const handleSendMessage = useCallback(
        async ({ overrideText, phaseOverride, skipUserMessage, intentData } = {}) => {
            const messageText = overrideText || (deps.inputValueRef?.current ?? '').trim();
            if (!messageText) return;

            if (!skipUserMessage) {
                const userMessage = {
                    id: `msg-${Date.now()}`,
                    role: 'user',
                    content: messageText,
                    timestamp: new Date(),
                };
                setMessages((prev) => [...prev, userMessage]);
                setInputValue('');
                if (inputRef?.current) {
                    inputRef.current.textContent = '';
                }
            }

            setError(null);
            if (deps.setPendingWidgetRef?.current) deps.setPendingWidgetRef.current(null);
            setPendingPiiResolution?.(null);
            setPiiSearchQueries?.({});

            const phase = phaseOverride || currentPhase;
            setIsTyping(true);
            let streamMsgId;

            try {
                const availableDataSources = AVAILABLE_DATA_SOURCES.map((source) => ({
                    id: source.id,
                    label: source.label,
                    description: source.description,
                    metricCategory: source.metricCategory,
                    dataType: source.dataType,
                    availableContexts: source.availableContexts,
                    availablePeriods: source.availablePeriods,
                    defaultColor: source.defaultColor,
                }));

                let requestBody;

                if (phase === 'intent') {
                    const summaries = generateWidgetSummaries(state.report.widgets, state.report.layout.lg || []);
                    const currentMessages = messagesRef?.current ?? [];
                    requestBody = {
                        message: messageText,
                        conversationHistory: getRecentHistory(currentMessages, 5),
                        context: pageContext,
                        phase: 'intent',
                        conversationId: deps.conversationIdRef?.current,
                        exchangeId: exchangeIdRef.current,
                    };
                } else {
                    const summaries = generateWidgetSummaries(state.report.widgets, state.report.layout.lg || []);
                    const currentMessages = messagesRef?.current ?? [];
                    requestBody = {
                        message: messageText,
                        conversationHistory: getFullHistory(currentMessages),
                        context: pageContext,
                        phase: 'execute',
                        mcpToolInfo: intentData
                            ? {
                                  action: intentData.action,
                                  server: intentData.server,
                                  tool: intentData.tool,
                                  arguments: intentData.arguments,
                              }
                            : undefined,
                        conversationId: deps.conversationIdRef?.current,
                        exchangeId: exchangeIdRef.current,
                    };

                    const widgetIdToUse = deps.pendingWidgetId ?? pendingWidgetIdRef?.current;
                    if (widgetIdToUse) {
                        const widget = state.report.widgets[widgetIdToUse] ?? null;
                        const layout = state.report.layout.lg?.find((item) => item.i === widgetIdToUse) ?? null;
                        if (widget) {
                            requestBody.currentWidget = widget;
                            if (layout) requestBody.currentLayout = layout;
                        }
                    }
                }

                streamMsgId = `msg-${Date.now()}-${phase}-${Math.random().toString(36).slice(2, 9)}`;
                const response = await fetchUnifiedAIStream(requestBody, {
                    onThought: (text) => {
                        setMessages((prev) => {
                            const exists = prev.some((m) => m.id === streamMsgId);
                            if (!exists) {
                                return [
                                    ...prev,
                                    {
                                        id: streamMsgId,
                                        role: 'assistant',
                                        content: '',
                                        thoughts: [text],
                                        timestamp: new Date(),
                                        dismissed: false,
                                    },
                                ];
                            }
                            return prev.map((m) =>
                                m.id === streamMsgId ? { ...m, thoughts: [...(m.thoughts || []), text] } : m,
                            );
                        });
                    },
                });
                const data = response?.result;
                const conversationId = response?.conversation_id;
                const exchangeId = response?.exchange_id;

                if (conversationId && deps.conversationIdRef && !deps.conversationIdRef.current) {
                    deps.conversationIdRef.current = conversationId;
                }
                if (exchangeId && exchangeId != exchangeIdRef.current) exchangeIdRef.current = exchangeId;

                posthog.capture(POSTHOG_EVENTS.FAE_MESSAGE_SENT, {
                    user_id: userId,
                    school_id: schoolId,
                    conversation_id: conversationId || deps.conversationIdRef?.current || null,
                    successful: true,
                });

                const currentAction = data?.action; //
                const sanitiseObj = data?.sanitise_obj;
                const fallbackToolChosen = data?.arguments?.fallback_reason; // attribute that outlines why 'fallback_tool' has been chosen
                const feedbackIds =
                    conversationId || exchangeId
                        ? { conversationId: conversationId || null, exchangeId: exchangeId || null }
                        : undefined;

                console.log('data', data);

                if (phase === 'intent') {
                    setCurrentPhase('execute');

                    if (data.intent === 'edit' && data.matchingWidgets?.length === 1) {
                        setPendingWidgetId(data.matchingWidgets[0].id);
                        if (pendingWidgetIdRef) pendingWidgetIdRef.current = data.matchingWidgets[0].id;
                    }

                    setMessages((prev) => {
                        const streamed = prev.some((m) => m.id === streamMsgId);
                        const thoughtsFromApi = normalizeThoughts(data.thoughts);
                        if (streamed) {
                            return prev.map((m) =>
                                m.id === streamMsgId
                                    ? {
                                          ...m,
                                          content: data.message,
                                          suggestedPrompts: data.suggestedPrompts,
                                          feedbackIds,
                                          thoughts:
                                              m.thoughts?.length > 0
                                                  ? m.thoughts
                                                  : thoughtsFromApi.length > 0
                                                    ? thoughtsFromApi
                                                    : m.thoughts,
                                      }
                                    : m,
                            );
                        }
                        return [
                            ...prev,
                            {
                                id: streamMsgId,
                                role: 'assistant',
                                content: data.message,
                                timestamp: new Date(),
                                dismissed: false,
                                suggestedPrompts: data.suggestedPrompts,
                                feedbackIds,
                                ...(thoughtsFromApi.length > 0 && { thoughts: thoughtsFromApi }),
                            },
                        ];
                    });

                    if (sanitiseObj?.has_pii === true) {
                        setPendingPiiResolution?.({
                            sanitizedMessage: sanitiseObj.sanitized_message,
                            entities: sanitiseObj.entities || {},
                        });
                        setInputValue(sanitiseObj.sanitized_message);
                        setCurrentPhase('intent');
                        setIsTyping(false);
                        return;
                    }

                    if (fallbackToolChosen || currentAction == 'direct_response') {
                        setCurrentPhase('intent');
                        return;
                    }

                    // await handleSendMessageRef.current?.({
                    //     overrideText: messageText,
                    //     phaseOverride: 'execute',
                    //     skipUserMessage: true,
                    //     intentData: data,
                    // });
                    setCurrentPhase('intent');
                    return;
                }

                const thoughtsFromApi = normalizeThoughts(data.thoughts);
                setMessages((prev) => {
                    const streamed = prev.some((m) => m.id === streamMsgId);
                    const editPayload =
                        data.intent === 'edit'
                            ? {
                                  widget: data.widget,
                                  layout: data.layout,
                                  message: data.message,
                                  changes: data.changes,
                              }
                            : undefined;
                    if (streamed) {
                        return prev.map((m) =>
                            m.id === streamMsgId
                                ? {
                                      ...m,
                                      content: data.message,
                                      edit: editPayload,
                                      suggestedPrompts: data.suggestedPrompts,
                                      feedbackIds,
                                      thoughts:
                                          m.thoughts?.length > 0
                                              ? m.thoughts
                                              : thoughtsFromApi.length > 0
                                                ? thoughtsFromApi
                                                : m.thoughts,
                                  }
                                : m,
                        );
                    }
                    return [
                        ...prev,
                        {
                            id: streamMsgId,
                            role: 'assistant',
                            content: data.message,
                            timestamp: new Date(),
                            edit: editPayload,
                            dismissed: false,
                            suggestedPrompts: data.suggestedPrompts,
                            feedbackIds,
                            ...(thoughtsFromApi.length > 0 && { thoughts: thoughtsFromApi }),
                        },
                    ];
                });
                setCurrentPhase('intent');
                setPendingWidgetId(null);
                if (pendingWidgetIdRef) pendingWidgetIdRef.current = null;
            } catch (err) {
                console.error('AI API error:', err);
                posthog.capture(POSTHOG_EVENTS.FAE_MESSAGE_SENT, {
                    user_id: userId,
                    school_id: schoolId,
                    conversation_id: deps.conversationIdRef?.current || null,
                    successful: false,
                });
                if (streamMsgId) {
                    setMessages((prev) => prev.filter((m) => m.id !== streamMsgId));
                }
                // setError(err?.message || 'Failed to communicate with Fae. Please try again.');
                const errorMessage = {
                    id: `msg-${Date.now() + 1}`,
                    role: 'assistant',
                    content: "I'm sorry, I encountered an error while processing your request. Please try again.",
                    timestamp: new Date(),
                };
                setMessages((prev) => [...prev, errorMessage]);
                setCurrentPhase('intent');
            } finally {
                setIsTyping(false);
            }
        },
        [
            currentPhase,
            setMessages,
            setCurrentPhase,
            setPendingWidgetId,
            getRecentHistory,
            getFullHistory,
            setError,
            setPendingPiiResolution,
            setPiiSearchQueries,
            setInputValue,
            setIsTyping,
            state,
            pageContext,
            deps.conversationIdRef,
            deps.pendingWidgetId,
            pendingWidgetIdRef,
            inputRef,
            exchangeIdRef,
            userId,
            schoolId,
        ],
    );

    handleSendMessageRef.current = handleSendMessage;

    return { handleSendMessage };
}
